import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { fx } from './motion-control.js';
import { ATTR_SCOPE } from './reveal.js';

const ATTR_SPLIT = 'data-split';
const SUPPORTED_TYPES = ['chars', 'words', 'lines'];

const DEFAULTS = {
    type: 'words',
    duration: 1,
    ease: 'power2.out',
    stagger: 0.05,
    y: 100,
    markers: false,
    once: true,
    autoSplit: true,
};

const SplitTextController = {
    ATTR_SPLIT,
    __ready: false,

    init() {
        this.__ready = this.__registerPlugin();
        return this.__ready;
    },

    setup(element, options = {}) {
        if (!element) return null;
        if (!this.__ready && !this.__registerPlugin()) return null;

        const config = this.__buildConfig(element, options);

        return ScrollTrigger.create({
            trigger: element,
            start: config.start,
            end: config.end,
            once: config.once,
            markers: config.markers,
            onEnter: () => this.__animate(element, config),
        });
    },

    collectTargets(scope) {
        const root = scope || (typeof document !== 'undefined' ? document : null);
        if (!root) return [];
        if (root.nodeType === 1 && root.hasAttribute?.(ATTR_SPLIT)) {
            return [root];
        }
        return Array.from(root.querySelectorAll?.(`[${ATTR_SPLIT}]`) || []);
    },

    play(element, options = {}) {
        if (!element) return null;
        if (!this.__ready && !this.__registerPlugin()) return null;

        const config = this.__buildConfig(element, options);
        config.once = false;
        return this.__animate(element, config);
    },

    __registerPlugin() {
        if (this.__ready) return true;
        if (!SplitText) {
            console.warn('[split-text] SplitText plugin not found. Check GSAP installation.');
            return false;
        }
        try {
            gsap.registerPlugin(ScrollTrigger, SplitText);
            this.__ready = true;
        } catch (error) {
            console.warn('[split-text] Impossible to register SplitText', error);
            this.__ready = false;
        }
        return this.__ready;
    },

    __buildConfig(element, overrides = {}) {
        const { type, primary } = this.__resolveType(element, overrides.type);
        const { start, end } = this.__resolveBounds(element, overrides.start, overrides.end);

        return {
            type,
            primary,
            start,
            end,
            once: this.__toBoolean(overrides.once ?? element.dataset?.splitOnce, DEFAULTS.once),
            markers: this.__toBoolean(
                overrides.markers ?? element.dataset?.splitMarkers,
                DEFAULTS.markers
            ),
            duration: this.__toNumber(
                overrides.duration ?? element.dataset?.splitDuration,
                DEFAULTS.duration
            ),
            ease: overrides.ease ?? element.dataset?.splitEase ?? DEFAULTS.ease,
            stagger: this.__parseStagger(
                overrides.stagger ?? element.dataset?.splitStagger,
                DEFAULTS.stagger
            ),
            y: this.__toNumber(overrides.y ?? element.dataset?.splitY, DEFAULTS.y),
            autoSplit: this.__toBoolean(
                overrides.autoSplit ??
                    element.dataset?.splitAuto ??
                    element.dataset?.splitAutosplit,
                DEFAULTS.autoSplit
            ),
            split: overrides.splitOptions || {},
        };
    },

    __resolveType(element, override) {
        const attr = element.getAttribute(ATTR_SPLIT);
        if (attr && attr !== 'on' && attr !== 'done' && !element.dataset.splitOriginal) {
            element.dataset.splitOriginal = attr;
        }

        const raw =
            override ||
            element.dataset.splitOriginal ||
            (attr && attr !== 'on' && attr !== 'done' ? attr : null) ||
            DEFAULTS.type;

        const tokens = Array.isArray(raw)
            ? raw
            : String(raw)
                  .split(',')
                  .map((token) => token.trim().toLowerCase())
                  .filter(Boolean);

        const filtered = tokens.filter((token) => SUPPORTED_TYPES.includes(token));
        const requested = filtered.length ? filtered : ['words'];

        const expanded = new Set();
        requested.forEach((token) => {
            if (token === 'chars') {
                expanded.add('words');
                expanded.add('chars');
            } else if (token === 'lines') {
                expanded.add('lines');
                expanded.add('words');
            } else {
                expanded.add('words');
            }
        });

        const order = ['lines', 'words', 'chars'];
        const typeTokens = order.filter((token) => expanded.has(token));

        return {
            type: typeTokens.join(', ') || DEFAULTS.type,
            primary: requested.includes('chars')
                ? 'chars'
                : requested.includes('words')
                ? 'words'
                : 'lines',
        };
    },

    __resolveBounds(element, startOverride, endOverride) {
        const scope = element.closest?.(`[${ATTR_SCOPE}]`);
        return {
            start: startOverride || scope?.getAttribute('data-anim-start') || 'top 90%',
            end: endOverride || scope?.getAttribute('data-anim-end') || 'bottom 10%',
        };
    },

    __animate(element, config) {
        const originalText = element.textContent?.trim();
        const splitVars = { type: config.type, ...config.split };

        let lastTween = null;
        const runAnimation = (instance, manageRevert = true) => {
            lastTween = this.__runAnimation(element, instance, config, originalText, manageRevert);
            return lastTween;
        };

        if (config.autoSplit) {
            const userOnSplit = typeof splitVars.onSplit === 'function' ? splitVars.onSplit : null;
            splitVars.autoSplit = true;
            splitVars.onSplit = (self) => {
                const tween = runAnimation(self, true);
                if (userOnSplit) {
                    const userResult = userOnSplit(self, tween);
                    return userResult ?? tween;
                }
                return tween;
            };

            const instances = SplitText.create(element, splitVars);
            const splitInstance = Array.isArray(instances) ? instances[0] : instances;
            return lastTween ?? runAnimation(splitInstance, true);
        }

        const instances = SplitText.create(element, splitVars);
        const splitInstance = Array.isArray(instances) ? instances[0] : instances;
        return runAnimation(splitInstance, true);
    },

    __runAnimation(element, splitInstance, config, originalText, manageRevert) {
        if (originalText) {
            element.setAttribute('aria-label', originalText);
        }

        const targets = this.__selectTargets(splitInstance, config.primary);

        if (!targets?.length) {
            if (manageRevert) {
                splitInstance?.revert?.();
            }
            this.__restoreAria(element, originalText);
            return null;
        }

        const previousAttr = element.getAttribute(ATTR_SPLIT);
        element.setAttribute(ATTR_SPLIT, 'on');

        const handleComplete = () => {
            if (manageRevert) {
                splitInstance?.revert?.();
            }
            this.__restoreAria(element, originalText);

            if (previousAttr && previousAttr !== 'on') {
                element.setAttribute(ATTR_SPLIT, 'done');
            } else {
                element.removeAttribute(ATTR_SPLIT);
            }
        };

        const tween = fx.from(targets, {
            y: config.y,
            autoAlpha: 0,
            ease: config.ease,
            duration: config.duration,
            stagger: config.stagger,
            onComplete: handleComplete,
        });

        if (!tween) {
            handleComplete();
        }

        return tween;
    },

    __selectTargets(instance, primary) {
        if (!instance) return [];
        if (primary === 'lines') return instance.lines;
        if (primary === 'words') return instance.words;
        return instance.chars;
    },

    __restoreAria(element, original) {
        if (original) {
            element.removeAttribute('aria-label');
        }
    },

    __toNumber(value, fallback) {
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
            const next = Number(value.trim());
            if (Number.isFinite(next)) return next;
        }
        return fallback;
    },

    __toBoolean(value, fallback) {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'string') {
            if (value === 'true') return true;
            if (value === 'false') return false;
        }
        return fallback;
    },

    __parseStagger(value, fallback) {
        if (value == null) return fallback;
        if (typeof value === 'number' || typeof value === 'object') return value;
        if (typeof value !== 'string') return fallback;

        const trimmed = value.trim();
        if (!trimmed) return fallback;

        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                const normalized = trimmed
                    .replace(
                        /([{,]\s*)([a-zA-Z0-9_]+)\s*:/g,
                        (_, prefix, key) => `${prefix}"${key}":`
                    )
                    .replace(/'/g, '"');
                return JSON.parse(normalized);
            } catch {
                return fallback;
            }
        }

        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? numeric : fallback;
    },
};

SplitTextController.init();

if (typeof window !== 'undefined') {
    const anim = (window.Anim = window.Anim || {});
    anim.splitText = SplitTextController;
}

export const setupSplitTrigger = SplitTextController.setup.bind(SplitTextController);
export const collectSplitTargets = SplitTextController.collectTargets.bind(SplitTextController);

export { ATTR_SPLIT };
export default SplitTextController;
